<template>
  <div class="my-ads-page">
    <b-card
      no-body
      class="my-ads-card"
    >
      <div class="my-ads-toolbar">
        <div class="toolbar-left">
          <b-form-select
            v-model="selectedCount"
            :options="countOptions"
            class="count-select"
          />
          <b-button
            class="create-btn"
            :disabled="deletingAdIds.length > 0"
            @click="openCreateAdModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span>Create New Ad</span>
          </b-button>
        </div>

        <div class="toolbar-right">
          <b-form-input
            v-model="search"
            placeholder="Search ads by name..."
            class="search-input"
          />
          <b-form-input
            v-model="selectedDate"
            type="date"
            class="date-input"
          />
          <b-dropdown
            right
            variant="outline-secondary"
            class="select-dropdown"
            :text="selectedFilter"
          >
            <b-dropdown-item @click="selectedFilter = 'Select'">
              Select
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Active'">
              Active
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Pending'">
              Pending
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Rejected'">
              Rejected
            </b-dropdown-item>
            <b-dropdown-item @click="selectedFilter = 'Expired'">
              Expired
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        responsive
        class="my-ads-table mb-0"
        :fields="tableFields"
        :items="rows"
        :busy="isLoadingAds"
        show-empty
        :empty-text="isLoadingAds ? '' : 'No ads found'"
      >
        <template #table-busy>
          <div class="my-ads-table-loader">
            <b-spinner
              variant="primary"
              label="Loading ads"
            />
            <span class="my-ads-table-loader__text">Loading ads...</span>
          </div>
        </template>

        <template #cell(adName)="{ item }">
          <div
            class="ad-cell ad-cell-link"
            @click="openAdDetail(item)"
          >
            <div
              class="ad-thumb"
              :class="{ 'ad-thumb--has-image': item.thumbnail }"
            >
              <img
                v-if="item.thumbnail"
                :src="resolveMediaUrl(item.thumbnail)"
                :alt="item.adName || 'Ad thumbnail'"
                class="ad-thumb-img"
              >
              <template v-else>
                <span>P</span>
                <span>O</span>
                <span>D</span>
                <span>A</span>
              </template>
            </div>
            <span class="ad-title">{{ item.adName || '-' }}</span>
          </div>
        </template>

        <template #cell(status)="{ item }">
          <span
            class="status-badge"
            :class="`status-${item.statusKey}`"
          >
            {{ item.status }}
          </span>
        </template>

        <template #cell(actions)="{ item }">
          <div class="action-icons">
            <img
              src="/svg/ads/edit.svg"
              alt="Edit ad"
              class="action-icon-img cursor-pointer"
              @click="openEditAd(item)"
            >
            <feather-icon
              v-if="!deletingAdIds.includes(item.id)"
              icon="Trash2Icon"
              size="15"
              class="cursor-pointer"
              :class="{ 'text-muted': deletingAdIds.includes(item.id) }"
              @click="deleteAd(item)"
            />
            <b-spinner
              v-else
              small
              label="Deleting"
            />
          </div>
        </template>
      </b-table>

      <div class="table-footer">
        <small class="table-footer__label">Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRows }} entries</small>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          class="mb-0 mypoda-pagination"
        >
          <template #prev-text>
            Previous
          </template>
          <template #next-text>
            Next
          </template>
        </b-pagination>
      </div>
    </b-card>

    <b-modal
      v-model="showCreateAdModal"
      hide-header
      hide-footer
      centered
      size="xl"
      modal-class="create-ad-modal"
      body-class="p-0"
    >
      <div class="create-ad-modal-shell">
        <button
          type="button"
          class="create-ad-modal-close"
          aria-label="Close create ad"
          @click="closeCreateAdModal"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </button>
        <create-ad-wizard
          v-if="showCreateAdModal"
          embedded
          @cancel="closeCreateAdModal"
          @submitted="onCreateAdSubmitted"
        />
      </div>
    </b-modal>

    <vendor-outcome-modal
      :visible.sync="showOutcomeModal"
      :variant="outcomeVariant"
      @primary="onOutcomePrimary"
    />
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormInput,
  BFormSelect,
  BTable,
  BPagination,
  BDropdown,
  BDropdownItem,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import CreateAdWizard from '@/features/ads/components/CreateAdWizard.vue'
import VendorOutcomeModal from '@/components/vendor/VendorOutcomeModal.vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { extractApiErrorMessage, normalizeAdStatusKey } from '@/utils/adCreate'
import { confirmAction } from '@/utils/uiActions'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export default {
  components: {
    BCard,
    BButton,
    BFormInput,
    BFormSelect,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    BModal,
    BSpinner,
    CreateAdWizard,
    VendorOutcomeModal,
  },
  data() {
    return {
      showCreateAdModal: false,
      showOutcomeModal: false,
      outcomeVariant: 'ad-submitted',
      _adStatusById: {},
      search: '',
      selectedDate: '',
      selectedFilter: 'Select',
      selectedCount: '08',
      countOptions: ['08', '12', '20', '50'],
      currentPage: 1,
      perPage: 8,
      tableFields: [
        { key: 'no', label: 'NO' },
        { key: 'adName', label: 'ADS LIST' },
        { key: 'brandName', label: 'BRAND' },
        { key: 'date', label: 'DATE' },
        { key: 'status', label: 'STATUS' },
        { key: 'expireDate', label: 'EXPIRE DATE' },
        { key: 'actions', label: 'ACTIONS' },
      ],
      rows: [],
      totalRows: 0,
      isLoadingAds: true,
      deletingAdIds: [],
    }
  },
  mounted() {
    this.fetchMyAds()
    this.openCreateFromRoute()
    this.startAdsRefreshPolling()
  },
  beforeDestroy() {
    this.stopAdsRefreshPolling()
  },
  computed: {
    paginationStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationEnd() {
      if (!this.totalRows) return 0
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  watch: {
    currentPage() {
      this.fetchMyAds()
    },
    selectedCount(newVal) {
      this.perPage = Number(newVal)
      this.currentPage = 1
      this.fetchMyAds()
    },
    search() {
      this.currentPage = 1
      this.fetchMyAds()
    },
    selectedFilter() {
      this.currentPage = 1
      this.fetchMyAds()
    },
    selectedDate() {
      this.currentPage = 1
      this.fetchMyAds()
    },
    '$route.query.create': function routeQueryCreateWatcher() {
      this.openCreateFromRoute()
    },
  },
  methods: {
    resolveMediaUrl,
    startAdsRefreshPolling() {
      this.stopAdsRefreshPolling()
      this._adsRefreshTimer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          this.fetchMyAds({ silent: true })
        }
      }, 60000)
      this._adsVisibilityHandler = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          this.fetchMyAds({ silent: true })
        }
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this._adsVisibilityHandler)
      }
    },
    stopAdsRefreshPolling() {
      if (this._adsRefreshTimer) {
        clearInterval(this._adsRefreshTimer)
        this._adsRefreshTimer = null
      }
      if (typeof document !== 'undefined' && this._adsVisibilityHandler) {
        document.removeEventListener('visibilitychange', this._adsVisibilityHandler)
        this._adsVisibilityHandler = null
      }
    },
    openCreateFromRoute() {
      if (this.$route.query.create !== '1') return
      this.showCreateAdModal = true
      this.$router.replace({ name: 'my-ads' }).catch(() => {})
    },
    onOutcomePrimary(variant) {
      if (variant === 'ad-rejected') {
        this.$router.push({ name: 'create-ad' }).catch(() => {})
        return
      }
      if (variant === 'ad-approved' || variant === 'ad-submitted') {
        this.$router.push({ name: 'my-ads' }).catch(() => {})
      }
    },
    detectAdStatusTransitions(ads) {
      const previous = this._adStatusById || {}
      const next = {}
      let transitionVariant = null

      ads.forEach(item => {
        const id = item.id
        const statusKey = normalizeAdStatusKey(item.status)
        if (id != null) {
          next[id] = statusKey
          const prior = previous[id]
          if (prior && prior !== statusKey) {
            if (['approved', 'active'].includes(statusKey)) {
              transitionVariant = 'ad-approved'
            } else if (statusKey === 'rejected') {
              transitionVariant = 'ad-rejected'
            }
          }
        }
      })

      this._adStatusById = next
      if (transitionVariant) {
        this.outcomeVariant = transitionVariant
        this.showOutcomeModal = true
      }
    },
    async fetchMyAds() {
      this.isLoadingAds = true
      try {
        const response = await vendorPanelApi.myAds({
          page: this.currentPage,
          per_page: this.perPage,
          search: this.search,
          status: this.selectedFilter === 'Select' ? '' : this.selectedFilter.toLowerCase(),
          ...(this.selectedDate ? { date: this.selectedDate } : {}),
        })
        const data = response?.data?.data || {}
        const ads = data.ads || []
        const total = Number(data.total)
        this.totalRows = Number.isFinite(total) && total >= 0 ? total : ads.length
        this.detectAdStatusTransitions(ads)
        const rowOffset = (this.currentPage - 1) * this.perPage
        this.rows = ads.map((item, index) => ({
          id: item.id,
          no: String(rowOffset + index + 1).padStart(2, '0'),
          adName: item.ad_name || '',
          brandName: item.brand_name || '—',
          thumbnail: item.thumbnail || '',
          date: item.created_date || '',
          status: item.status || '',
          statusKey: normalizeAdStatusKey(item.status),
          expireDate: item.expire_date || '',
        }))
      } catch (error) {
        this.rows = []
        this.totalRows = 0
      } finally {
        this.isLoadingAds = false
      }
    },
    openCreateAdModal() {
      this.showCreateAdModal = true
    },
    closeCreateAdModal() {
      this.showCreateAdModal = false
    },
    onCreateAdSubmitted() {
      this.showCreateAdModal = false
      this.outcomeVariant = 'ad-submitted'
      this.showOutcomeModal = true
      this.fetchMyAds()
    },
    async deleteAd(item) {
      if (this.deletingAdIds.includes(item.id)) return
      confirmAction(
        this,
        {
          title: 'Delete ad?',
          text: 'This action cannot be undone.',
          confirmButtonText: 'Delete',
        },
        async () => {
          this.deletingAdIds.push(item.id)
          try {
            await vendorPanelApi.deleteAd({ ad_id: item.id })
            this.fetchMyAds()
            this.$swal({
              title: 'Deleted',
              text: 'Ad deleted successfully',
              icon: 'success',
              timer: 1600,
              showConfirmButton: false,
            })
          } catch (error) {
            this.$swal({
              title: 'Error',
              text: extractApiErrorMessage(error, 'Unable to delete ad'),
              icon: 'error',
              timer: 2200,
              showConfirmButton: false,
            })
          } finally {
            this.deletingAdIds = this.deletingAdIds.filter(id => id !== item.id)
          }
        },
      )
    },
    openAdDetail(item) {
      this.$router.push({
        name: 'my-ads-detail',
        params: {
          id: item.id,
        },
      })
    },
    openEditAd(item) {
      this.$router.push({
        name: 'edit-ad',
        params: {
          id: item.id,
        },
      })
    },
  },
}
</script>

<style scoped lang="scss">
.my-ads-page {
  .my-ads-card {
    border: 1px solid #ebeaf1;
    border-radius: 8px;
    box-shadow: none;
    overflow: hidden;
  }

  .my-ads-toolbar {
    padding: 0.9rem 1rem 0.7rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .count-select {
    width: 72px;
  }

  .create-btn {
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-weight: 600;
    padding: 0.53rem 1rem;

    &:hover,
    &:focus,
    &:active {
      background: #011425 !important;
      border-color: #011425 !important;
      color: #fff !important;
    }
  }

  .search-input {
    width: 145px;
  }

  .date-input {
    width: 155px;
    height: 36px;
  }

  .my-ads-table {
    min-height: 280px;

    ::v-deep .b-table-busy-slot {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.88);
      z-index: 2;
    }

    ::v-deep .table-responsive {
      position: relative;
      min-height: 280px;
    }

    ::v-deep table {
      margin-bottom: 0;
      border-top: 1px solid #ebeaf1;
    }

    ::v-deep thead th {
      background: #fff;
      color: #7a758f;
      font-size: 0.76rem;
      font-weight: 700;
      border-top: 0;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
    }

    ::v-deep tbody td {
      color: #5e5873;
      vertical-align: middle;
      font-size: 0.92rem;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
    }
  }

  .my-ads-table-loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 2rem 1rem;
  }

  .my-ads-table-loader__text {
    color: #5e5873;
    font-size: 0.92rem;
    font-weight: 500;
  }

  .ad-cell {
    display: flex;
    align-items: center;
    gap: 0.62rem;
  }

  .ad-cell-link {
    cursor: pointer;
  }

  .ad-thumb {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    border: 1px solid #123456;
    flex-shrink: 0;

    &--has-image {
      display: block;
      background: #f4f4f8;
    }

    .ad-thumb-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    span {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #011425;
      color: #fff;
      font-size: 0.55rem;
      font-weight: 700;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  .ad-title {
    color: #5e5873;
  }

  .status-badge {
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .status-active {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.16);
  }

  .status-pending {
    color: #7367f0;
    background: rgba(115, 103, 240, 0.16);
  }

  .status-pending_approval {
    color: #7367f0;
    background: rgba(115, 103, 240, 0.16);
  }

  .status-draft {
    color: #82868b;
    background: rgba(130, 134, 139, 0.16);
  }

  .status-inactive,
  .status-stopped {
    color: #82868b;
    background: rgba(130, 134, 139, 0.16);
  }

  .status-rejected {
    color: #ea5455;
    background: rgba(234, 84, 85, 0.16);
  }

  .status-expired {
    color: #ff9f43;
    background: rgba(255, 159, 67, 0.16);
  }

  .action-icons {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    color: #6f6b86;
  }

  .action-icon-img {
    width: 15px;
    height: 15px;
    object-fit: contain;
    display: block;
  }

  .table-footer {
    padding: 0.8rem 0.95rem 0.95rem;
    border-top: 1px solid #ebeaf1;
  }

  .create-ad-modal-shell {
    position: relative;
  }
}
</style>
<style lang="scss">
.create-ad-modal {
  .modal-dialog {
    max-width: 1100px !important;
    width: calc(100% - 2rem);
    margin: 1.75rem auto !important;
  }

  .modal-content {
    border: 0;
    border-radius: 12px;
    overflow: hidden;
  }

  .create-ad-modal-shell {
    position: relative;
  }

  .create-ad-modal-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    left: auto;
    z-index: 10;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: #667085;
    line-height: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:focus {
      color: #344054;
      background: transparent;
      outline: none;
      box-shadow: none;
    }
  }
}
</style>
